/**
 * description: SIP 电话封装类
 * dateTime: 2025/6/11-11:42
 * author: ZhangJun <1580043700@qq.com>
 */

import JsSIP from 'jssip'
import { showFloatingWindow } from './FloatingWindow.tsx'

type SipCallConstructor = {
  userName: string
  password: string
  domain: string
}

class SIPCall {
  ua
  session
  duringTheCall = false
  registered = false
  connected = false

  constructor() {}

  init({ userName, password, domain }: SipCallConstructor): void {
    if (this.ua != null) {
      return
    }
    const uri = `sip:${userName}@${domain}`
    const socket = new JsSIP.WebSocketInterface(`wss://${domain}/sip`)
    const configuration = {
      sockets: [socket],
      uri: uri,
      password,
      contact_uri: uri,
    }

    this.ua = new JsSIP.UA(configuration)

    this.ua.on('connected', () => {
      console.log('[UA] connected')
      this.connected = true
    })
    this.ua.on('disconnected', () => {
      console.log('[UA] disconnected')
      this.connected = false
    })
    this.ua.on('registered', () => {
      console.log('[UA] registered')
      this.registered = true
    })
    this.ua.on('unregistered', () => {
      console.log('[UA] unregistered')
      this.registered = false
    })
    this.ua.on('registrationFailed', e => {
      console.error('[UA] registrationFailed:', e.cause)
      this.registered = false
    })

    this.ua.start()
  }

  call(
    phone: string,
    options?: { clueId?: string; failed: () => void; ended: () => void; accepted: () => void }
  ) {
    const waitingAudio = playWaitingAudio()
    const failAudio = playFailAudio()
    return new Promise((resolve, reject) => {
      if (!this.registered) {
        reject('[UA] no registered')
        return
      }
      if (this.duringTheCall) {
        reject('[UA] 正在通话中')
        return
      }
      this.duringTheCall = true
      openAudio()
        .then(() => {
          const eventHandlers = {
            progress: e => {
              waitingAudio.play()
              console.log('[Call] in progress', e)
            },
            failed: e => {
              console.error('[Call] failed with cause:', e)

              waitingAudio.pause()
              waitingAudio.currentTime = 0
              failAudio.play()
              reject(e)
            },
            ended: e => {
              waitingAudio.pause()
              waitingAudio.currentTime = 0

              console.log('[Call] ended with cause:', e.data?.cause || 'unknown')
            },
            confirmed: e => {
              console.log('[Call] confirmed')

              waitingAudio.pause()
              waitingAudio.currentTime = 0

              const remoteStream = new MediaStream()
              const pc = this.session.connection // RTCPeerConnection

              pc.getReceivers().forEach(receiver => {
                if (receiver.track && receiver.track.kind === 'audio') {
                  remoteStream.addTrack(receiver.track)
                }
              })

              const audio = new Audio()
              audio.srcObject = remoteStream
              audio.autoplay = true
              audio.onplay = err => {
                console.log('自动播放音频onplay：', err)
              }
              audio.play().catch(err => {
                console.error('自动播放音频失败：', err)
              })
              resolve(e)
            },
          }

          const option = {
            eventHandlers,
            extraHeaders: [`X-XSID: ${options?.clueId}`],
            mediaConstraints: { audio: true, video: false },
          }

          this.session = this.ua.call(`sip:${phone}@cc.shjmall.cn`, option)

          showFloatingWindow(this.session)

          this.session.on('progress', () => {
            console.log('📡 正在振铃...')
          })

          this.session.on('accepted', () => {
            console.log('✅ 对方已接听')
            options?.accepted?.()
            this.duringTheCall = true
          })

          this.session.on('confirmed', () => {
            console.log('🔗 媒体通道已建立')
            this.duringTheCall = true
          })

          this.session.on('failed', e => {
            this.duringTheCall = false
            options?.failed?.()
            console.log('❌ 呼叫失败', e.cause)
          })

          this.session.on('ended', e => {
            this.duringTheCall = false
            options?.ended?.()
            console.log('⛔ 通话已结束', e.cause)
          })
        })
        .catch(err => {
          this.duringTheCall = false
          reject(err)
        })
    })
  }

  hangup() {
    if (this.session) {
      this.session.terminate()
      console.log('[Call] session terminated')
    } else {
      console.warn('[Call] No active session to terminate.')
    }
  }

  destroy() {
    this.hangup()
    if (this.ua) {
      this.ua.stop()
      this.ua = null
      console.log('[UA] destroy')
    }
  }
}

const openAudio = () => {
  return navigator.mediaDevices.getUserMedia({ audio: true })
}

const playWaitingAudio = () => {
  const waitingAudio = new Audio(
    'https://prod-shj.oss-cn-chengdu.aliyuncs.com/shj/202506/a90b04d9-d180-4297-a1a1-99a8e140ce4d.mp3'
  )
  waitingAudio.loop = true
  return waitingAudio
}
const playFailAudio = () => {
  return new Audio(
    'https://prod-shj.oss-cn-chengdu.aliyuncs.com/shj/202506/f93b5da5-66a7-49c3-9657-379a4ae9cd42.mp3'
  )
}
const instance = new SIPCall()
export default instance
